import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type { PlayerProfile } from "@wr/contracts";
import { fetchProfile, fetchSession, type SessionUser } from "../lib/auth";
import {
  getAuthTransition,
  setAuthTransition,
  subscribeAuthTransition,
} from "../lib/auth-transition";
import { IdentityContext, identityOf } from "../lib/identity-scope";
import { stageFor, type Stage } from "./routing";

/**
 * Who is signed in, as one answer the whole app shares.
 *
 * ── Why this is not TanStack Query ──────────────────────────────────────────
 *
 * It could be, and Query is already in the app. But the session is the thing
 * every route guard blocks on, and Query's `isPending` for a *shared* key
 * behaves differently depending on which component mounted first. The splash's
 * anti-flash rule (§5, LOCKED: nothing renders if auth resolves in under
 * 250ms) needs a single, explicit, app-level "have we asked yet", and that is
 * easier to get right — and to read — as one piece of state than as a cache
 * policy. Query keeps the per-screen data; this keeps identity.
 *
 * `reload()` is the only way the answer changes. Every screen that mutates
 * identity (login, sign-up, guest sign-in, conversion, `/welcome`, log out)
 * calls it, so there is exactly one place the server's view of the player is
 * turned into the client's.
 */

export type SessionStatus = "loading" | "resolved" | "error";

export interface SessionState {
  status: SessionStatus;
  session: SessionUser | null;
  profile: PlayerProfile | null;
  stage: Stage;
  /** Transport failure on the last attempt — drives the offline banner. */
  offline: boolean;
  /**
   * Re-reads the session and the profile.
   *
   * **Returns whether the read landed** (Q10, §14.3). It cannot *reject* — a
   * transport failure is deliberately not "logged out" and is reported as
   * `offline` — but the settlement reconcile has to tell *"we checked"* from
   * *"we could not check"*, because the coin chip's brightness is a claim about
   * money and §14.3 part 5 says a failed read dims and stays dimmed. A caller
   * that does not care ignores the boolean, which every existing one does.
   */
  reload: () => Promise<boolean>;
  /**
   * Optimistic local update after a mutation returns a fresh profile.
   *
   * **It refreshes the signed-in player; it never changes who that is**
   * (SEC-2, T-910). A profile carrying a different id than the one held — a
   * mutation that resolved after a log out, or after another account signed
   * in — is dropped: identity only ever changes through `reload()`, the one
   * read that asks the server who holds the cookie now.
   */
  setProfile: (profile: PlayerProfile | null) => void;
  /**
   * A player-chosen log out: stop holding the last player's generation, so
   * the next read without a session is nobody's. `useLogout` only.
   */
  forgetIdentity: () => void;
  /**
   * A conversion has succeeded and the guest session is gone (§14.1 r2).
   * Guards freeze rather than redirect while this is true.
   */
  justConverted: boolean;
  /** A player-chosen log-out is in flight (§14.1 ruling C) — guards drop `?next=`. */
  loggingOut: boolean;
  /**
   * Ends the conversion freeze: re-reads the session (now genuinely signed
   * out) and only then lowers the flag, so there is no render in between where
   * a guard can see a stale "ready" stage on the log-in screen.
   */
  finishConversion: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [session, setSession] = useState<SessionUser | null>(null);
  const [profile, setProfileState] = useState<PlayerProfile | null>(null);
  const [offline, setOffline] = useState(false);
  /**
   * The last player id a read returned — what keeps the identity generation (below)
   * steady across a signed-out read (the conversion handoff). Cleared only by
   * `forgetIdentity()`.
   */
  const [lastPlayerId, setLastPlayerId] = useState<string | null>(null);

  const transition = useSyncExternalStore(
    subscribeAuthTransition,
    getAuthTransition,
    getAuthTransition,
  );

  const load = useCallback(async (): Promise<boolean> => {
    try {
      const nextSession = await fetchSession();
      // The profile is only meaningful with a session, and asking for it
      // without one is a guaranteed 401 on every cold load.
      const nextProfile = nextSession ? await fetchProfile() : null;
      /**
       * The abort path for both transitions: a session came back, so whatever
       * was supposed to end it did not. Nothing is going to redirect, which
       * means nothing is going to reach `RequireAnonymous` to lower the flag —
       * and a flag left raised would quietly strip `?next=` off the next
       * genuine guard bounce. There is no race to lose here, precisely because
       * no guard will fire.
       */
      if (nextSession && getAuthTransition() !== "none") setAuthTransition("none");
      setSession(nextSession);
      setProfileState(nextProfile);
      if (nextProfile) setLastPlayerId(nextProfile.id);
      setOffline(false);
      setStatus("resolved");
      return true;
    } catch {
      // A transport failure is not "logged out" — treating it as one would log
      // a player out of a working session because a train went into a tunnel.
      setOffline(true);
      setStatus((current) => (current === "loading" ? "error" : current));
      return false;
    }
  }, []);

  /**
   * **§14.1 requirement 2, LOCKED — the refetch stands down mid-conversion.**
   *
   * The sheet used to `await reload()` on success, which is what actually
   * moved the page under it: the session came back `null`, the stage flipped
   * to `anonymous`, and the guard on the frozen screen redirected. There is
   * nothing to learn from that request — we already know the guest session was
   * revoked, because we are the ones who asked for it — so it is not made.
   */
  const reload = useCallback(async (): Promise<boolean> => {
    /**
     * Mid-conversion the refetch stands down (§14.1 r2) — and it reports
     * `false`, because *"we did not ask"* is not *"we checked"*. A reconcile
     * that ran into this window holds its dim rather than asserting a number
     * nothing verified.
     */
    if (getAuthTransition() === "converting") return false;
    return load();
  }, [load]);

  /**
   * Re-reads the session after the handoff has been requested. It deliberately
   * does **not** lower the flag itself.
   *
   * React Router commits a `navigate()` as a *transition*, while a `setState`
   * from this fetch is a default-priority update. React will therefore render
   * the new session against the **old** location first — and a guard that saw
   * `anonymous` there, with the freeze already lifted, would redirect off the
   * screen the player is leaving and rewrite the `?next=` we spent this whole
   * ruling capturing. So the flag is lowered by `RequireAnonymous` once the app
   * has actually arrived, which is the only moment it is safe.
   */
  const finishConversion = useCallback(async () => {
    await load();
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  const setProfile = useCallback((next: PlayerProfile | null) => {
    setProfileState((current) =>
      next !== null && (current === null || current.id !== next.id) ? current : next,
    );
  }, []);

  const forgetIdentity = useCallback(() => setLastPlayerId(null), []);

  /**
   * **The identity generation — the account the client's private state
   * belongs to** (SEC-2, T-910; `lib/identity-scope.ts` has the why).
   *
   * The player id of the signed-in player, held **stickily** across a
   * signed-out read so the guest conversion's handoff (same player row before
   * and after) keeps its state. It moves only when a different player id
   * arrives, or when `forgetIdentity()` is called by a player-chosen log out.
   * Published on its own context (`IdentityContext`, read by `useIdentity()`),
   * which every private query key carries first (`hooks/usePrivateKey.ts`)
   * and on which `BalanceProvider` resets.
   */
  const identity = identityOf(profile?.id ?? lastPlayerId);

  const value = useMemo<SessionState>(
    () => ({
      status,
      session,
      profile,
      stage: stageFor(session, profile),
      offline,
      reload,
      setProfile,
      forgetIdentity,
      justConverted: transition === "converting",
      loggingOut: transition === "logging-out",
      finishConversion,
    }),
    [
      status,
      session,
      profile,
      offline,
      reload,
      setProfile,
      forgetIdentity,
      transition,
      finishConversion,
    ],
  );

  return (
    <SessionContext.Provider value={value}>
      <IdentityContext.Provider value={identity}>{children}</IdentityContext.Provider>
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside <SessionProvider>");
  return value;
}
